import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id || '0', 10);
  const article = getArticleById(id);

  const prevArticle = articles.find(a => a.id === id - 1);
  const nextArticle = articles.find(a => a.id === id + 1);

  // Schema.org structured data
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article?.title,
    description: article?.excerpt,
    image: article?.image,
    datePublished: article?.date,
    dateModified: article?.date,
    author: {
      '@type': 'Organization',
      name: '51黑料不打烊',
      url: 'https://51heilian.com/',
    },
    publisher: {
      '@type': 'Organization',
      name: '51黑料不打烊',
      url: 'https://51heilian.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://51heilian.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51heilian.com/article/${id}`,
    },
    articleSection: article?.category,
    keywords: article?.tags?.join(', '),
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://51heilian.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article?.category || '分类',
        item: `https://51heilian.com/category/${article?.categorySlug || ''}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article?.title || '文章',
        item: `https://51heilian.com/article/${id}`,
      },
    ],
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-heading text-foreground-950 mb-4">文章不存在</h1>
            <a href="/" className="text-primary-500 hover:text-primary-400 transition-colors">返回首页</a>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (n: number) => {
    if (n >= 10000) return (n / 10000).toFixed(1) + '万';
    return n.toString();
  };

  const formatComments = (n: number) => {
    if (n >= 10000) return (n / 10000).toFixed(1) + '万';
    return n.toString();
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />

      <main>
        <article className="pt-20 md:pt-28 pb-10 md:pb-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
              <a href="/" className="hover:text-accent-500 transition-colors">首页</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <a href={`/category/${article.categorySlug}`} className="hover:text-accent-500 transition-colors">{article.category}</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-accent-500 truncate max-w-[200px]">{article.title}</span>
            </nav>

            {/* Article Body */}
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              {/* Left - Media */}
              <div className="lg:w-[45%]">
                <div className="sticky top-24">
                  <span className="border border-primary-500/40 text-primary-400 bg-primary-500/10 tag-pill mb-4">
                    {article.category}
                  </span>
                  <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-accent-500/20">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="eager" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-primary-500/80 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors">
                        <i className="ri-play-fill text-background-50 text-xl ml-0.5"></i>
                      </div>
                      <span className="text-foreground-300 text-sm">点击播放视频</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="lg:w-[55%]">
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-6">
                  {article.excerpt}
                </p>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-8 pb-8 border-b border-background-200">
                  <div className="flex items-center gap-2 text-foreground-400 text-sm">
                    <i className="ri-calendar-line"></i>
                    <span>{article.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-foreground-400 text-sm">
                    <i className="ri-eye-line"></i>
                    <span>{formatViews(article.views)} 次阅读</span>
                  </div>
                  <div className="flex items-center gap-2 text-foreground-400 text-sm">
                    <i className="ri-chat-3-line"></i>
                    <span>{formatComments(article.comments)} 条评论</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {article.tags.map(tag => (
                      <span key={tag} className="bg-background-100 text-foreground-500 text-xs px-3 py-1 rounded-full">#{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Full Content */}
                <div className="prose prose-invert max-w-none">
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-8">
                    <div className="flex items-start gap-3 mb-6">
                      <div className="w-1 h-full min-h-[80px] bg-accent-500/40 rounded-full shrink-0"></div>
                      <div>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                          据知情人士独家向本平台爆料，{article.title.replace('？', '')}。该事件已在圈内引起广泛关注，多位业内人士对此事发表了看法。
                        </p>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                          目前相关证据正在进一步核实中。本平台将持续跟踪报道此事的最新进展。同时我们呼吁广大网友理性吃瓜，不信谣不传谣。
                        </p>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                          据悉，涉事方目前尚未对此事做出正式回应。但根据多位独立信源的交叉印证，该爆料的真实性较高。我们也将持续关注事态发展。
                        </p>
                        <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                          本文内容来源于网友投稿及公开信息整理，不代表本平台立场。如有不实之处，欢迎相关方提供证明材料进行申诉。
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Source attribution */}
                  <div className="bg-background-100 rounded-2xl p-5 md:p-6 mb-8">
                    <h4 className="text-foreground-950 font-heading text-sm mb-3">信息来源</h4>
                    <p className="text-foreground-500 text-xs md:text-sm">
                      本文整合自多位独立信源，包括但不限于：社交媒体公开信息、网友投稿、行业内部人士透露等。所有信息均经过多方交叉验证。
                    </p>
                  </div>
                </div>

                {/* Share & Actions */}
                <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-background-200">
                  <span className="text-foreground-400 text-sm">分享到：</span>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-500 flex items-center justify-center transition-colors duration-300 group">
                    <i className="ri-weibo-line text-foreground-500 group-hover:text-background-50 text-sm transition-colors"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-500 flex items-center justify-center transition-colors duration-300 group">
                    <i className="ri-wechat-line text-foreground-500 group-hover:text-background-50 text-sm transition-colors"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-500 flex items-center justify-center transition-colors duration-300 group">
                    <i className="ri-link text-foreground-500 group-hover:text-background-50 text-sm transition-colors"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Comments / Related Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100/50">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-accent-500 font-heading text-lg md:text-xl">〖 网友热议 〗</span>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="bg-background-50 border border-background-200 rounded-full px-5 py-2.5 flex items-center gap-3">
                <div className="flex items-center">
                  <i className="ri-star-fill text-accent-500 text-lg mr-0.5"></i>
                  <i className="ri-star-fill text-accent-500 text-lg mr-0.5"></i>
                  <i className="ri-star-fill text-accent-500 text-lg mr-0.5"></i>
                  <i className="ri-star-fill text-accent-500 text-lg mr-0.5"></i>
                  <i className="ri-star-half-line text-accent-500 text-lg"></i>
                </div>
                <span className="text-foreground-950 font-label font-bold text-lg">4.8</span>
                <span className="text-foreground-400 text-xs">({formatComments(article.comments)} 人评价)</span>
              </div>
            </div>

            {/* Comment Items */}
            <div className="space-y-4 md:space-y-5 max-w-3xl mx-auto mb-10">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-5 md:p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-full min-h-[60px] bg-accent-500/30 rounded-full shrink-0"></div>
                    <div className="flex-1">
                      <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-3">
                        {i === 1 && '终于等到实锤了！之前就感觉不对劲，现在真相大白。支持爆料人，让这些虚伪的人无处遁形！'}
                        {i === 2 && '说实话不太意外，圈内这种事情太多了。只是没想到这次被爆出来的证据这么硬核，坐等后续发展。'}
                        {i === 3 && '理性吃瓜，不站队。但是这些证据确实很有说服力，希望当事人能出来正面回应，而不是一味删帖。'}
                        {i === 4 && '说实话作为老粉真的很失望，但更希望事情能得到公正处理。如果是真的，那该道歉道歉，该退圈退圈。'}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full border-2 border-accent-500/30 bg-background-100 flex items-center justify-center">
                          <span className="text-foreground-400 text-xs font-label">
                            {['吃瓜群众甲', '深海鱼雷', '真相探员', '老粉一枚'][i - 1][0]}
                          </span>
                        </div>
                        <span className="text-foreground-600 text-xs font-label">
                          {['吃瓜群众甲', '深海鱼雷', '真相探员', '老粉一枚'][i - 1]}
                        </span>
                        <span className="text-foreground-300 text-xs">{['2小时前', '3小时前', '5小时前', '8小时前'][i - 1]}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Prev/Next Navigation */}
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              {prevArticle ? (
                <a href={`/article/${prevArticle.id}`} className="flex items-center gap-2 text-foreground-500 hover:text-foreground-950 transition-colors group">
                  <div className="w-10 h-10 rounded-full border border-foreground-400 flex items-center justify-center group-hover:border-foreground-950 transition-colors">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  <span className="text-sm hidden sm:inline">上一篇爆料</span>
                </a>
              ) : (
                <div></div>
              )}
              {nextArticle ? (
                <a href={`/article/${nextArticle.id}`} className="flex items-center gap-2 text-foreground-500 hover:text-foreground-950 transition-colors group">
                  <span className="text-sm hidden sm:inline">下一篇爆料</span>
                  <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                    <i className="ri-arrow-right-line text-background-50"></i>
                  </div>
                </a>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-8 md:mb-12">
              相关<strong className="text-primary-500">爆料</strong>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {articles
                .filter(a => a.id !== article.id && a.categorySlug === article.categorySlug)
                .slice(0, 4)
                .map((a) => (
                  <ArticleCard
                    key={a.id}
                    id={a.id}
                    title={a.title}
                    excerpt={a.excerpt}
                    category={a.category}
                    categorySlug={a.categorySlug}
                    image={a.image}
                    date={a.date}
                    views={a.views}
                    comments={a.comments}
                    tags={a.tags}
                  />
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}